import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { EntityOption } from '@/components/shared/entity-combobox';
import { api } from '@/lib/api';

interface MemberOption {
  id: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
}

export interface MemberPickerOption extends EntityOption {
  firstName: string;
  middleName: string;
  lastName: string;
}

export const useMemberPicker = (enabled: boolean, scope: string) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<EntityOption | null>(null);

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', scope, query],
    enabled,
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (query.trim()) params.set('q', query.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
  });

  const options = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: [member.firstName, member.middleName, member.lastName].filter(Boolean).join(' '),
        firstName: member.firstName,
        middleName: member.middleName?.trim() ?? '',
        lastName: member.lastName,
      })),
    [members.data?.data],
  );

  const select = (id: string | null) => {
    if (!id) {
      setSelected(null);
      return;
    }
    const next = options.find((row) => row.id === id);
    if (next) setSelected(next);
  };

  return {
    query,
    setQuery,
    options,
    selected,
    setSelected,
    select,
    loading: members.isLoading,
  };
};
